// KM-E9 / US-E9.2 · the PARAMETER CLASS surface (spec luku 7.3), as the API states it.
//
// This client exists so the settings pages can render the class table instead of re-stating it. Which
// class a parameter is in — and therefore whether it is editable, by whom, and after publish — is a
// server-side property (US-E9.2a, ADR-0007 §25): the SPA asks and renders the answer. A hardcoded list
// here would be a second source of truth that drifts silently from the enforcement, and the enforcement
// is the one that decides.
//
// Three reads and one write:
//   • GET  /api/questionnaires/parameters        — the STATIC class table (identical for every tenant).
//   • GET  /api/questionnaires/{id}/parameters   — the same table RESOLVED against one questionnaire:
//                                                  `settableNow`, the `reason` whenever that is false,
//                                                  and the value in force.
//   • GET  /api/questionnaires/templates         — the platform factor library (instrument templates).
//   • PUT  /api/questionnaires/{id}/tenant-parameters — the tenant-FREE write (language, open window,
//                                                  retention period).
//
// `settable` and `endpoint` are deliberately separate facts from `class`: reminders, branding and report
// distribution are tenant-free BY CLASS with no write surface built yet, and rendering those as "locked"
// would misstate the rule. The pages say "not configurable here yet" for them, never "locked".

import type { components } from "@clms/contracts";
import { apiFetch } from "../lib/api";

/** One parameter's classification — the static half, identical for every questionnaire and tenant. */
export type QuestionnaireParameterClassRow = components["schemas"]["QuestionnaireParameterDto"];
/** One parameter resolved against ONE questionnaire: settable now, why not, and the value in force. */
export type QuestionnaireParameterState = components["schemas"]["QuestionnaireParameterStateDto"];
/** Every parameter of one questionnaire, with the questionnaire's status and publish instant. */
export type QuestionnaireParameters = components["schemas"]["QuestionnaireParametersDto"];
/** The tenant-free write body: every field optional, at least one required (server-enforced). */
export type SaveTenantParametersRequest = components["schemas"]["SaveTenantParametersRequest"];
/** One built-in instrument template — the platform-level factor library (US-E5.1). */
export type QuestionnaireTemplate = components["schemas"]["QuestionnaireTemplateDto"];

/**
 * The five parameter classes of spec luku 7.3, in the spec's order (least delegated first). Used ONLY to
 * order and label the sections: which parameter belongs to which class always comes from the server, and
 * a class the server names that is missing here still renders — as its own group, under its raw code.
 */
export const PARAMETER_CLASS_ORDER = [
  "derived",
  "locked",
  "platform",
  "tenant_locked_at_publish",
  "tenant_free",
] as const;

/** The class whose parameters the PLATFORM operator owns (spec luku 7.3 "Alustataso"). */
export const PLATFORM_CLASS = "platform";

/** The static parameter class table — the taxonomy itself, as the API states it. */
export function listParameterClasses(signal?: AbortSignal): Promise<QuestionnaireParameterClassRow[]> {
  return apiFetch<QuestionnaireParameterClassRow[]>("/api/questionnaires/parameters", { signal });
}

/** The class table resolved against one questionnaire (settableNow + reason + value in force). */
export function getQuestionnaireParameters(
  id: string,
  signal?: AbortSignal,
): Promise<QuestionnaireParameters> {
  return apiFetch<QuestionnaireParameters>(`/api/questionnaires/${id}/parameters`, { signal });
}

/**
 * Change the questionnaire's TENANT-FREE parameters (language, open window, retention period) — the class
 * that survives publish. Anything of another class posted here is refused by name and class (422), never
 * silently dropped; a `closesAt` moved into the past on a live questionnaire, or a `retentionMonths` that
 * would put the purge deadline in the past, is refused with the deadline it would have set. Every refusal
 * is the server's to make: surface `problem.detail`, never pre-empt it.
 */
export function saveTenantParameters(
  id: string,
  body: SaveTenantParametersRequest,
  signal?: AbortSignal,
): Promise<components["schemas"]["QuestionnaireDto"]> {
  return apiFetch<components["schemas"]["QuestionnaireDto"]>(
    `/api/questionnaires/${id}/tenant-parameters`,
    { method: "PUT", body, signal },
  );
}

/** The built-in instrument templates — the platform-level factor library (identical for every tenant). */
export function listTemplates(signal?: AbortSignal): Promise<QuestionnaireTemplate[]> {
  return apiFetch<QuestionnaireTemplate[]>("/api/questionnaires/templates", { signal });
}

// ── US-E9.5 · the tenant's report-richness level thresholds ──────────────────────────────────────
//
// A TENANT-level parameter of class "Tenant – vapaa" (spec luku 10.1a) — one set governs every report
// the tenant runs, so it is not the questionnaire's own value even though it appears in the
// questionnaire's parameter table. It gates report RICHNESS and never k: the k floor stays locked, and
// the API refuses a gate below it.

/** The gates in force, plus whether they are the tenant's own or the shipped default. */
export type ReportingLevelThresholds = components["schemas"]["ReportingLevelThresholdsDto"];
/** The gates + their audited change history (who / when / from → to). */
export type ReportingLevelThresholdsView = components["schemas"]["ReportingLevelThresholdsViewDto"];
/** The gate write body — an omitted (null) gate is left where it is. */
export type SaveReportingLevelThresholdsRequest =
  components["schemas"]["SaveReportingLevelThresholdsRequest"];

/** The tenant's report-richness gates in force, with their audited change history. */
export function getReportingLevelThresholds(
  signal?: AbortSignal,
): Promise<ReportingLevelThresholdsView> {
  return apiFetch<ReportingLevelThresholdsView>("/api/reporting-parameters/level-thresholds", {
    signal,
  });
}

/**
 * Change the tenant's report-richness gates. A set that would drop a gate below the locked k floor, or
 * stop ascending, is refused with a 422 that writes nothing — the refusal is the server's, and its
 * `detail` is what the page shows.
 */
export function saveReportingLevelThresholds(
  body: SaveReportingLevelThresholdsRequest,
  signal?: AbortSignal,
): Promise<ReportingLevelThresholdsView> {
  return apiFetch<ReportingLevelThresholdsView>("/api/reporting-parameters/level-thresholds", {
    method: "PUT",
    body,
    signal,
  });
}
